import Cookies from 'cookies-js';
import ProductType from './application/ProductType.js';
import RedeemInvite from './application/RedeemInvite.js';
import RoleUser from './application/RoleUser.js';
import Invitation from './application/Invitation.js';
const { RoleType } = RoleUser;
const ROLE_TO_PRODUCT_MAP = {
    [RoleType.DASHBOARD]: {
        [ProductType.CHART]: true,
        [ProductType.SEEK]: true,
        [ProductType.WHEREHOUSE]: false,
    },
    [RoleType.DASHBOARD_TRIAL]: {
        [ProductType.CHART]: true,
        [ProductType.SEEK]: true,
        [ProductType.SIDEKICK]: false,
        [ProductType.WHEREHOUSE]: false,
    },
    [RoleType.SEEK]: {
        [ProductType.CHART]: false,
        [ProductType.SEEK]: true,
        [ProductType.SIDEKICK]: false,
        [ProductType.WHEREHOUSE]: false,
    },
    [RoleType.SEEK_TRIAL]: {
        [ProductType.CHART]: false,
        [ProductType.SEEK]: true,
        [ProductType.SIDEKICK]: false,
        [ProductType.WHEREHOUSE]: false,
    },
    [RoleType.SIDEKICK]: {
        [ProductType.CHART]: false,
        [ProductType.SEEK]: false,
        [ProductType.SIDEKICK]: true,
        [ProductType.WHEREHOUSE]: false,
    },
    [RoleType.WHEREHOUSE]: {
        [ProductType.CHART]: false,
        [ProductType.SEEK]: true,
        [ProductType.SIDEKICK]: false,
        [ProductType.WHEREHOUSE]: true,
    },
};
class PermissionManager {
    constructor() {
        this.roles = {
            all: [],
            active: [],
            expired: [],
        };
        const ready = {};
        // Configure a promise to use for "ready" indication
        const promise = new Promise((resolve, reject) => {
            ready.resolve = resolve;
            ready.reject = reject;
        });
        ready.promise = promise;
        this._ready = ready;
    }
    get ProductType() {
        return PermissionManager.ProductType;
    }
    /**
     * Denotes if user has organization scope with no scope PK
     * @type {boolean}
     */
    get hasUniversalOrganizationAccess() {
        return this.roles.active.some(role => role.isUniversalOrganization);
    }
    /**
     * @param {ApplicationUser} user
     * @return {Promise<PermissionManager>}
     */
    async loadFor(user) {
        return RoleUser.allForUser(user.id)
            .then(this.setRoles.bind(this));
    }
    /**
     * @return {Promise<PermissionManager>}
     */
    async ready() {
        return this._ready.promise;
    }
    /**
     * Retrieve the RoleUser the user is currently associated to
     * @return {?RoleUser}
     */
    getCurrentRoleUser() {
        const { active } = this.roles;
        if (active.length === 0)
            return null;
        // Ensures we only "act" as another organization for "universal" grants
        if (this.hasUniversalOrganizationAccess) {
            const id = this.getCurrentOrganizationID();
            const base = active.find(role => role.isUniversalOrganization);
            return new RoleUser({
                ...base,
                scopePk: id,
            });
        }
        // Assumed workflow
        if (active.length === 1) {
            return active[0];
        }
        // Edge case; multiple active roles
        const prioritized = this.getPrioritizedRoleUsers();
        return prioritized[0];
    }
    /**
     * Retrieve roles prioritized by level of access & expiration
     * @return {RoleUser[]}
     */
    getPrioritizedRoleUsers() {
        const { active } = this.roles;
        const valueMap = RoleUser.ROLE_VALUE_MAP;
        const prioritized = active.sort((lhs, rhs) => {
            var _a, _b, _c, _d;
            let change = valueMap[rhs.role] - valueMap[lhs.role];
            if (change === 0) {
                change = ((_b = (_a = rhs.expiredAt) === null || _a === void 0 ? void 0 : _a.valueOf()) !== null && _b !== void 0 ? _b : 0) - ((_d = (_c = lhs.expiredAt) === null || _c === void 0 ? void 0 : _c.valueOf()) !== null && _d !== void 0 ? _d : 0);
            }
            return change;
        });
        return prioritized;
    }
    /**
     * Retrieve the organization ID the user is associated to
     * @return {?number}
     */
    getCurrentOrganizationID() {
        var _a;
        const { active } = this.roles;
        if (active.length === 0)
            return null;
        // Ensures we only "act" as another organization for "universal" grants
        if (this.hasUniversalOrganizationAccess) {
            const preferred = Cookies.enabled ? Cookies.get(PermissionManager.PREFERRED_ORGANIZATION_COOKIE) : undefined;
            return preferred !== undefined ? parseInt(preferred, 10) : 1;
        }
        // Assumed workflow
        if (active.length === 1) {
            const role = active[0];
            return role.scopePk;
        }
        // Edge case; multiple active roles
        // Return one if all the same
        const ID = (_a = active[0]) === null || _a === void 0 ? void 0 : _a.scopePk;
        const sharedID = active.every((role) => role.scopePk === ID);
        if (sharedID) {
            return ID;
        }
        const priorityOrder = this.getPrioritizedRoleUsers();
        const [prioritized] = priorityOrder.filter((roleUser) => roleUser.isOrganizationScope);
        return prioritized === null || prioritized === void 0 ? void 0 : prioritized.scopePk;
    }
    /**
     * @param {ProductType} [productType]
     * @return {boolean}
     */
    canAccessProduct(productType) {
        if (!productType)
            return this.roles.active.length > 0;
        return this.roles.active.some((role) => {
            const roleMap = ROLE_TO_PRODUCT_MAP[role.role] || {};
            return roleMap[productType];
        });
    }
    /**
     * @param {number} organizationID
     * @param {ProductType} [productType]
     * @return {boolean}
     */
    canAccessContentFrom(organizationID, productType) {
        return this.roles.active.some((role) => {
            const productMatch = !productType || this.canAccessProduct(productType);
            const scopeMatch = (role.isOrganizationScope
                && (role.isUniversal || role.scopePk === organizationID));
            return productMatch && scopeMatch;
        });
    }
    /**
     * @param {string} code
     * @return {Promise<RoleUser>}
     */
    async redeemInvitation(code) {
        return RedeemInvite.create({ code })
            .then((roleUser) => {
            const props = roleUser;
            const role = new RoleUser(props);
            this.addRole(role);
            return role;
        });
    }
    /**
     * @param {RoleUser} role
     */
    addRole(role) {
        this.roles.all.push(role);
        const target = role.isActive ? 'active' : 'expired';
        this.roles[target].push(role);
    }
    /**
     * @param {RoleUser[]} roles
     * @return {PermissionManager}
     */
    setRoles(roles) {
        this.roles = {
            all: [],
            active: [],
            expired: [],
        };
        roles.forEach(this.addRole.bind(this));
        this._ready.resolve(this);
        return this;
    }
    /**
     * @param {object} environment
     * @param {string} environment.domain
     * @param {string} environment.protocol
     */
    static configure({ domain, protocol }) {
        PermissionManager.RoleUser.$AR.url = `${protocol}://data.${domain}/api/permissions/v1/role-users`;
        PermissionManager.RedeemInvite.$AR.url = `${protocol}://data.${domain}/api/permissions/v1/redeem-invite`;
        PermissionManager.Invitation.$AR.url = `${protocol}://data.${domain}/api/org/v1/invitations`;
    }
    static get PREFERRED_ORGANIZATION_COOKIE() {
        return 'sw.permissions.preferred';
    }
}
PermissionManager.RoleUser = RoleUser;
PermissionManager.RedeemInvite = RedeemInvite;
PermissionManager.Invitation = Invitation;
PermissionManager.ProductType = ProductType;
export default PermissionManager;
